import { create } from "zustand";
import type { Uuid } from "@shared/api";

/**
 * Что вкладка помнит о себе между запусками: ширина левой колонки и место,
 * на котором её закрыли.
 *
 * Одним модулем, а не двумя: обе величины — про расположение, а не про
 * данные, обе читаются один раз при запуске и обе лежат в одном ключе
 * хранилища. К самому приложению ни та ни другая отношения не имеет; живут
 * они дольше входа — почти, место при выходе забывается, см. `forgetPlace`.
 *
 * Стором zustand по той же причине, что и громкость: значение нужно и на
 * отрисовке, и вне её — шов тянут указателем, а не перерисовкой. Своего
 * списка подписчиков заводить не надо, их тут уже было пять и они разошлись
 * (см. шапку `realtime/volume.ts`).
 */

const KEY = "airel.remember";

/**
 * Пределы ширины.
 *
 * Снизу — чтобы имя канала со значком и счётчиком непрочитанного помещалось
 * хоть как-то, сверху — чтобы колонка не съела ленту. Узкое окно стережёт
 * отдельно `max-width` в разметке: оно меняется без нас, и пересчитывать его
 * на каждое изменение размера значило бы держать то же правило дважды.
 */
export const NAV_MIN = 200;
export const NAV_MAX = 460;

interface Remembered {
  /** Ширина левой колонки. `null` — не трогали, берётся из макета. */
  width: number | null;
  /** Последний открытый сервер. `null` — личные переписки. */
  server: Uuid | null;
  /** Последний открытый канал: канал сервера или переписка — по месту. */
  channel: Uuid | null;
  /**
   * Последняя открытая переписка — отдельно от канала: с ней возвращаются в
   * личные из сервера, как домой в Discord, а канал к тому времени уже
   * серверный.
   */
  direct: Uuid | null;
}

const NOTHING: Remembered = { width: null, server: null, channel: null, direct: null };

export function clampNav(width: number): number {
  return Math.min(Math.max(Math.round(width), NAV_MIN), NAV_MAX);
}

/*
 * Читается с оглядкой: в хранилище лежит то, что туда положили прошлые
 * сборки и, при желании, руки человека. Ширина за пределами разумного
 * заклинила бы колонку, а не число на её месте — уронило бы разметку.
 */
function read(): Remembered {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...NOTHING };

    const saved = JSON.parse(raw) as Partial<Remembered>;

    return {
      width: typeof saved.width === "number" ? clampNav(saved.width) : null,
      server: typeof saved.server === "string" ? saved.server : null,
      channel: typeof saved.channel === "string" ? saved.channel : null,
      direct: typeof saved.direct === "string" ? saved.direct : null,
    };
  } catch {
    return { ...NOTHING };
  }
}

export const useRemember = create<Remembered>()(() => read());

function save() {
  try {
    localStorage.setItem(KEY, JSON.stringify(useRemember.getState()));
  } catch {
    // Переполненное хранилище — не повод бросать работу: расположение
    // доживёт до конца сеанса в памяти.
  }
}

/** Ширина колонки. `null` — вернуть ту, что в макете. */
export function setNavWidth(width: number | null) {
  useRemember.setState({ width: width === null ? null : clampNav(width) });
  save();
}

/**
 * Где мы сейчас.
 *
 * Зовётся с каждой сменой места, поэтому молчит, когда менять нечего:
 * запись в хранилище синхронная, и делать её на всякий случай не стоит.
 */
export function rememberPlace(server: Uuid | null, channel: Uuid | null) {
  const now = useRemember.getState();
  if (now.server === server && now.channel === channel) return;

  useRemember.setState({ server, channel });
  save();
}

/**
 * Половинки того же места.
 *
 * Порознь они нужны затем, чтобы место запоминал тот, кто его меняет: сервер
 * переключают одной дверью, канал — другой, и каждой из них известна только
 * своя половина. Пока запись была одна на обе, звать её мог лишь тот, кто
 * видит сразу обе, — то есть эффект, глядящий на результат.
 */
export function rememberServer(server: Uuid | null) {
  rememberPlace(server, useRemember.getState().channel);
}

export function rememberChannel(channel: Uuid | null) {
  rememberPlace(useRemember.getState().server, channel);
}

/** Открыли переписку — с ней и вернутся в личные. */
export function rememberDirect(direct: Uuid | null) {
  if (useRemember.getState().direct === direct) return;
  useRemember.setState({ direct });
  save();
}

/**
 * Забыть место при выходе.
 *
 * Ширина остаётся: она про экран, а не про человека, и следующему вошедшему
 * на той же вкладке скорее пригодится, чем помешает. Место — наоборот:
 * чужой сервер, открытый чужим входом, — то же самое, что чужие переписки в
 * списке, и убирается вместе с ними.
 */
export function forgetPlace() {
  rememberPlace(null, null);
  rememberDirect(null);
}
